import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Download, FileText, Check, X, AlertCircle, Clock,
  Users, RefreshCw, ChevronDown, Loader2, FileSpreadsheet, Trash2, Mail
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badges
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-700',
    processing: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700'
  };
  
  return (
    <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

// Import Progress Component
const ImportProgress = ({ job, onRefresh }) => {
  const progress = job.total_rows > 0 
    ? Math.round((job.processed_rows / job.total_rows) * 100) 
    : 0;

  useEffect(() => {
    if (job.status === 'processing') {
      const interval = setInterval(onRefresh, 2000);
      return () => clearInterval(interval);
    }
  }, [job.status, onRefresh]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-gray-500" />
          <span className="font-medium text-gray-900">{job.file_name || 'Import'}</span>
          <StatusBadge status={job.status} />
        </div>
        {job.status === 'processing' && (
          <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
        )}
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ${
            job.status === 'completed' ? 'bg-green-500' :
            job.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
      
      <div className="flex justify-between text-sm text-gray-500">
        <span>{job.processed_rows} / {job.total_rows} rows</span>
        <span>
          {job.success_count > 0 && <span className="text-green-600">{job.success_count} success</span>}
          {job.error_count > 0 && <span className="text-red-600 ml-2">{job.error_count} errors</span>}
        </span>
      </div>
      
      {/* Email Status */}
      {job.email_status && job.email_status !== 'skipped' && (
        <div className="mt-2 flex items-center gap-2 text-sm">
          <Mail className="w-4 h-4 text-gray-400" />
          <span className={
            job.email_status === 'completed' ? 'text-green-600' :
            job.email_status === 'sending' ? 'text-blue-600' :
            job.email_status === 'failed' ? 'text-red-600' : 'text-gray-500'
          }>
            {job.email_status === 'completed' ? `${job.emails_sent || 0} emails sent` :
             job.email_status === 'sending' ? `Sending emails... (${job.emails_sent || 0} sent)` :
             job.email_status === 'pending' ? 'Emails pending' :
             job.email_status === 'failed' ? 'Email sending failed' : job.email_status}
          </span>
        </div>
      )}
      
      {job.errors && job.errors.length > 0 && (
        <div className="mt-3 p-3 bg-red-50 rounded-lg">
          <p className="text-sm font-medium text-red-700 mb-2">Errors:</p>
          <ul className="text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto">
            {job.errors.map((err, idx) => (
              <li key={idx}>Row {err.row}: {err.error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Main Bulk Import Component
const BulkImportPage = () => {
  const { eventId } = useParams();
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedTicketType, setSelectedTicketType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [event, setEvent] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [dragActive, setDragActive] = useState(false);

  // Fetch event and ticket types
  const fetchEventData = useCallback(async () => {
    try {
      const [eventRes, ticketsRes] = await Promise.all([
        fetch(`${API}/universal-events/events/${eventId}`),
        fetch(`${API}/universal-events/events/${eventId}/ticket-types?include_hidden=true`)
      ]);
      
      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
      }
      
      if (ticketsRes.ok) {
        const tickets = await ticketsRes.json();
        setTicketTypes(tickets);
        if (tickets.length > 0 && !selectedTicketType) {
          setSelectedTicketType(tickets[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching event data:', error);
    }
  }, [eventId, selectedTicketType]);

  // Fetch import jobs
  const fetchJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/universal-events/events/${eventId}/imports`);
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  }, [eventId]);

  // Refresh a specific job
  const refreshJob = useCallback(async (jobId) => {
    try {
      const res = await fetch(`${API}/universal-events/events/${eventId}/import/${jobId}`);
      if (res.ok) {
        const updated = await res.json();
        setJobs(prev => prev.map(j => j.job_id === jobId ? updated : j));
      }
    } catch (error) {
      console.error('Error refreshing job:', error);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEventData();
    fetchJobs();
  }, [fetchEventData, fetchJobs]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        toast.error('Please drop a CSV file');
        return;
      }
      setFile(droppedFile);
    }
  };

  // Upload and import
  const handleImport = async () => {
    if (!file || !selectedTicketType) {
      toast.error('Please select a file and ticket type');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(
        `${API}/universal-events/events/${eventId}/import/attendees?ticket_type_id=${selectedTicketType}&send_email=${sendEmail}`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Import started: ${result.total_rows} rows`);
        setFile(null);
        fetchJobs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setUploading(false);
    }
  };

  // Download template CSV
  const downloadTemplate = () => {
    const template = 'name,email,phone,organization,job_title,country\nJohn Doe,john@example.com,+254700000000,Acme Corp,Manager,Kenya';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendee_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="bulk-import-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">Bulk Import Attendees</h1>
          <p className="text-sm text-gray-500">{event?.name || 'Loading...'}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Upload Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload CSV File
          </h2>

          {/* Ticket Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign to Ticket Type *
            </label>
            <select
              value={selectedTicketType}
              onChange={(e) => setSelectedTicketType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500"
              data-testid="ticket-type-select"
            >
              <option value="">Select ticket type...</option>
              {ticketTypes.map(tt => (
                <option key={tt.id} value={tt.id}>
                  {tt.name} - {tt.currency} {tt.price.toLocaleString()}
                </option>
              ))}
            </select>
          </div>

          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-green-500 bg-green-50' : 
              file ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8 text-green-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600 mb-2">
                  Drag and drop your CSV file here, or{' '}
                  <label className="text-green-600 cursor-pointer hover:underline">
                    browse
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </label>
                </p>
                <p className="text-sm text-gray-400">
                  CSV format: name, email, phone, organization, job_title, country
                </p>
              </>
            )}
          </div>

          {/* Options */}
          <div className="mt-4 flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="w-4 h-4 text-green-600 rounded"
              />
              <span className="text-sm text-gray-700">Send confirmation emails to imported attendees</span>
            </label>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-between">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            
            <Button
              onClick={handleImport}
              disabled={!file || !selectedTicketType || uploading}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="start-import-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Start Import
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Import History */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Import History
            </h2>
            <Button variant="ghost" size="sm" onClick={fetchJobs}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {loadingJobs ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Loading history...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8">
              <FileSpreadsheet className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No imports yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map(job => (
                <ImportProgress 
                  key={job.job_id} 
                  job={job} 
                  onRefresh={() => refreshJob(job.job_id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
          <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            CSV Format Requirements
          </h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Required columns:</strong> name, email</li>
            <li>• <strong>Optional columns:</strong> phone, organization, job_title, country</li>
            <li>• First row must be column headers</li>
            <li>• Email addresses must be valid and unique</li>
            <li>• For large imports (100+ rows), progress will update automatically</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default BulkImportPage;
